/**
 * @file        src/pages/account/BalanceSheet.jsx
 * @description Balance Sheet (Account Management → Balance Sheet). A single
 *              chronological ledger consolidating Flight, Hotel and Insurance
 *              bookings, with opening + running balance, KPI tiles, and filters
 *              for Financial Year, Date Range and Booking Type. Exports the
 *              loaded sheet to CSV or Excel (.xls).
 * @author      Kinetika Labs
 * @created     2026-07-04
 * @changelog
 *   - 2026-07-04 Claude — Initial (modelled on the legacy admin's sheet).
 *   - 2026-07-23 Claude — Add Date Range + Booking Type filters and an Excel
 *                          (.xls) export beside CSV; label Flight/Hotel/Insurance.
 *   - 2026-07-23 Claude — Sheet now consolidates Flight + Hotel + Insurance. Hotel
 *                          & insurance money lives in their own tables (not
 *                          `payments`), so only flight rows showed before; the
 *                          backend UNION ledger now feeds all three here.
 *   - 2026-07-24 Claude — Use the themed DatePicker for From/To (was the native
 *                          <input type="date">, which didn't match the admin UI).
 *   - 2026-08-10 Claude — To date can no longer precede From date (matches the
 *                          same min={from} guard already used on the Booking
 *                          List date filters). Console-wide date rule pass.
 *   - 2026-09-15 Claude — Picking a From date after the chosen To clears To, instead of sending a from > to range the route rejects (400).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import TableSkeleton from '../../components/TableSkeleton';
import { get } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { inr, formatDate } from '../../lib/format';

// Booking type → human label. `flow_type` is 'flight' | 'hotel' | 'insurance'
// (the payments table was widened for standalone insurance policies).
const KIND = { flight: 'Flight', hotel: 'Hotel', insurance: 'Insurance' };

function rowDetail(r) {
  const kind = KIND[r.flow_type] || 'Flight';
  const what = r.status === 'Refunded' ? 'Booking Refund' : 'Booking';
  return `${kind} ${what} (ID - ${r.booking_id ?? '—'})`;
}

// Which instrument moved a ledger row's money. Flight rows come from two sources
// — a gateway capture and its booking's wallet half — so `src` names them. Hotel
// and insurance rows take their debit from total_paid, which spans BOTH
// instruments, so they read as a mix unless one side is zero.
function instrumentLabel(r) {
  if (r.src === 'wallet') return 'Wallet';
  if (r.src === 'gateway') return 'Gateway';
  const wallet = Number(r.wallet_amount) || 0;
  const gateway = Number(r.gateway_amount) || 0;
  if (wallet > 0 && gateway > 0) return 'Wallet + Gateway';
  if (wallet > 0) return 'Wallet';
  return 'Gateway';
}

// The export rows (opening + ledger + totals) shared by both CSV and Excel, so
// the two downloads always agree with each other and with the on-screen table.
function exportRows(sheet) {
  return [
    { id: '', date: '', detail: 'Opening Balance', paidWith: '', debit: '', credit: '', balance: sheet.opening_balance },
    ...sheet.rows.map((r) => ({
      id: r.id,
      date: formatDate(r.created_at),
      detail: rowDetail(r),
      // Which instrument moved this money — wallet, gateway, or a booking row
      // that spans both (hotel / insurance take their debit from total_paid).
      paidWith: instrumentLabel(r),
      debit: r.debit_amount || 0,
      credit: r.credit_amount || 0,
      balance: r.balance,
    })),
    { id: '', date: '', detail: 'Totals', paidWith: '', debit: sheet.total_debit, credit: sheet.total_credit, balance: sheet.closing_balance },
  ];
}

// A stable filename stem describing the window that was exported.
function exportStem(sheet) {
  if (sheet.from || sheet.to) return `${sheet.from || 'start'}_to_${sheet.to || 'end'}`;
  return sheet.fy;
}

function download(blob, filename) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportCsv(sheet) {
  if (!sheet) return;
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    ['ID', 'Date', 'Transaction Detail', 'Paid With', 'Debit', 'Credit', 'Balance'].join(','),
    ...exportRows(sheet).map((r) => [r.id, esc(r.date), esc(r.detail), esc(r.paidWith), r.debit, r.credit, r.balance].join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  download(blob, `balance-sheet-${exportStem(sheet)}.csv`);
}

// Excel via the SpreadsheetML 2003 XML dialect — no dependency, opens in Excel
// without the "format doesn't match extension" warning that an HTML .xls gives,
// and keeps money/ID cells as real numbers (not text).
function exportExcel(sheet) {
  if (!sheet) return;
  const xesc = (v) => String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cell = (v, numeric) =>
    (numeric && v !== '' && v != null)
      ? `<Cell><Data ss:Type="Number">${Number(v)}</Data></Cell>`
      : `<Cell><Data ss:Type="String">${xesc(v)}</Data></Cell>`;
  const row = (cells) => `<Row>${cells.join('')}</Row>`;

  const header = row(['ID', 'Date', 'Transaction Detail', 'Paid With', 'Debit', 'Credit', 'Balance'].map((h) => cell(h)));
  const body = exportRows(sheet).map((r) => row([
    cell(r.id, true), cell(r.date), cell(r.detail), cell(r.paidWith),
    cell(r.debit, true), cell(r.credit, true), cell(r.balance, true),
  ]));

  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Balance Sheet">
  <Table>
   ${[header, ...body].join('\n   ')}
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  download(blob, `balance-sheet-${exportStem(sheet)}.xls`);
}

const BOOKING_TYPES = [
  { value: '', label: 'All bookings' },
  { value: 'flight', label: 'Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'insurance', label: 'Insurance' },
];

export default function BalanceSheet() {
  const [fy, setFy] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [flowType, setFlowType] = useState('');

  // A custom date range overrides the FY server-side, so only send `fy` when no
  // range is active — keeps the request (and the cache key) honest.
  const rangeActive = Boolean(from || to);
  const params = {
    ...(fy && !rangeActive ? { fy } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(flowType ? { flowType } : {}),
  };

  const { data: sheet, isLoading } = useQuery({
    queryKey: qk.transactions.balanceSheet(params),
    queryFn: () => get('/transactions/balance-sheet', { params }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const rows = sheet?.rows || [];
  const years = sheet?.years || [];

  const clearRange = () => { setFrom(''); setTo(''); };

  return (
    <>
      <PageHead
        title="Balance Sheet"
        subtitle="Consolidated ledger · Account Management"
        actions={
          <>
            <button className="btn ghost" onClick={() => exportCsv(sheet)} disabled={!sheet}>
              <Icon name="download" /> CSV
            </button>
            <button className="btn primary" onClick={() => exportExcel(sheet)} disabled={!sheet}>
              <Icon name="download" /> Excel
            </button>
          </>
        }
      />

      <div className="mini-kpi">
        <article className="mk glass neo"><small>Total Transactions</small><b>{sheet?.total_count ?? '—'}</b></article>
        <article className="mk glass neo"><small>Total Credit</small><b className="cre">{sheet ? inr(sheet.total_credit) : '—'}</b></article>
        <article className="mk glass neo"><small>Total Debit</small><b className="deb">{sheet ? inr(sheet.total_debit) : '—'}</b></article>
        {/* The debit total by instrument — these two add up to Total Debit. */}
        <article className="mk glass neo"><small>From Wallet</small><b>{sheet ? inr(sheet.total_wallet) : '—'}</b></article>
        <article className="mk glass neo"><small>From Gateway</small><b>{sheet ? inr(sheet.total_gateway) : '—'}</b></article>
        <article className="mk glass neo"><small>Current Balance</small><b>{sheet ? inr(sheet.closing_balance) : '—'}</b></article>
      </div>

      <article className="panel glass">
        <div className="search-row">
          <div className="field">
            <label>Financial Year</label>
            <Select
              value={fy || sheet?.fy || ''}
              onChange={(v) => setFy(v)}
              options={years.map((y) => ({ value: y, label: y }))}
            />
          </div>
          <div className="field">
            <label>From</label>
            <DatePicker value={from} onChange={(v) => { setFrom(v); if (to && v && v > to) setTo(''); }} />
          </div>
          <div className="field">
            <label>To</label>
            <DatePicker value={to} onChange={(v) => setTo(v)} min={from} />
          </div>
          <div className="field">
            <label>Booking Type</label>
            <Select value={flowType} onChange={(v) => setFlowType(v)} options={BOOKING_TYPES} />
          </div>
          {rangeActive && (
            <button className="btn ghost" type="button" onClick={clearRange}>
              <Icon name="x" /> Clear dates
            </button>
          )}
        </div>
        {rangeActive && (
          <p className="muted" style={{ margin: '0 0 12px' }}>
            Showing the selected date range — the Financial Year filter is ignored while dates are set.
          </p>
        )}

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>ID</th><th>Date</th><th>Transaction Detail</th><th>Paid With</th><th>Debit</th><th>Credit</th><th>Balance</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td /><td />
                  <td className="muted">Opening Balance</td>
                  <td /><td /><td />
                  <td className="mono">{sheet ? inr(sheet.opening_balance) : '—'}</td>
                </tr>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No transactions for these filters.</td></tr>
                ) : rows.map((r) => (
                  // Rows are unioned from four sources, so ids can collide both
                  // across products AND within flight (a gateway capture and its
                  // booking's wallet half) — key on src + flow_type + id.
                  <tr key={`${r.src ?? 'booking'}-${r.flow_type}-${r.id}`}>
                    <td className="mono">{r.id}</td>
                    <td className="muted">{formatDate(r.created_at)}</td>
                    <td>{rowDetail(r)}</td>
                    <td className="muted">{instrumentLabel(r)}</td>
                    <td className="deb">{r.debit_amount > 0 ? inr(r.debit_amount) : '—'}</td>
                    <td className="cre">{r.credit_amount > 0 ? inr(r.credit_amount) : '—'}</td>
                    <td className="mono">{inr(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td /><td />
                  <td><b>Totals</b></td>
                  {/* The debit total, split by instrument — the two add up to it. */}
                  <td className="muted">
                    {sheet ? `${inr(sheet.total_wallet)} wallet · ${inr(sheet.total_gateway)} gateway` : '—'}
                  </td>
                  <td className="deb"><b>{sheet ? inr(sheet.total_debit) : '—'}</b></td>
                  <td className="cre"><b>{sheet ? inr(sheet.total_credit) : '—'}</b></td>
                  <td className="mono"><b>{sheet ? inr(sheet.closing_balance) : '—'}</b></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
